import React, { useState, useEffect, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Coins, Mail, ChevronRight, Sparkles, Wallet, CreditCard, Bitcoin, Globe,
  Loader2, AlertTriangle, ArrowLeft, ShieldCheck,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { donateApi } from "../api/auth";
import { useContentObject } from "../hooks/useContentData";

const METHODS = [
  { id: "paypal",    name: "PayPal",            currency: "USD", icon: Wallet, desc: "Tarjeta o saldo PayPal" },
  { id: "dlocal_go", name: "dLocal Go",         currency: "USD", icon: Globe, desc: "Tarjetas y medios locales en tu pais (USD)" },
  { id: "paymentwall", name: "Paymentwall",     currency: "USD", icon: Globe, desc: "Pagos internacionales — tarjetas y metodos locales" },
  { id: "mp_ar",     name: "MercadoPago AR",    currency: "ARS", icon: CreditCard, desc: "Tarjeta / dinero en cuenta / Rapipago" },
  { id: "mp_cl",     name: "MercadoPago CL",    currency: "CLP", icon: CreditCard, desc: "Tarjeta chilena / dinero en cuenta" },
  { id: "khipu_cl",  name: "Khipu Chile",       currency: "CLP", icon: CreditCard, desc: "Transferencia bancaria chilena" },
  { id: "khipu_ar",  name: "Khipu Argentina",   currency: "ARS", icon: CreditCard, desc: "Transferencia bancaria argentina" },
  { id: "flow_cl",   name: "Flow Chile",        currency: "CLP", icon: CreditCard, desc: "Webpay, tarjetas y transferencia (Chile)" },
  { id: "crypto",    name: "USDT (Crypto)",     currency: "USD", icon: Bitcoin, desc: "USDT TRC20 / BEP20 / ERC20" },
];

const CURRENCY_META = {
  USD: { symbol: "$", label: "USD" },
  ARS: { symbol: "$", label: "ARS" },
  CLP: { symbol: "$", label: "CLP" },
};

const DEFAULT_RATES = {
  usd:  { amount: 1,    coins: 1500 },
  usdt: { amount: 1,    coins: 1500 },
  ars:  { amount: 1,    coins: 1 },
  clp:  { amount: 1000, coins: 1500 },
};

function parseRatePair(cfg, key) {
  const def = DEFAULT_RATES[key] || { amount: 1, coins: 1 };
  const amount = parseFloat(cfg?.[`rate_${key}_amount`]);
  const coins = parseFloat(cfg?.[`rate_${key}_coins`]);
  const a = Number.isFinite(amount) && amount > 0 ? amount : def.amount;
  const c = Number.isFinite(coins) && coins >= 0 ? coins : def.coins;
  return { amount: a, coins: c, ratio: c / a };
}

function rateForMethod(cfg, methodId, currency) {
  if (methodId === "crypto") return parseRatePair(cfg, "usdt");
  const key = String(currency || "usd").toLowerCase();
  return parseRatePair(cfg, key);
}

const BONUS_TABLES = {
  USD: [
    { min: 0,    max: 0.99,  bonus: 0  },
    { min: 1,    max: 9.99,  bonus: 5  },
    { min: 10,   max: 24.99, bonus: 10 },
    { min: 25,   max: 49.99, bonus: 15 },
    { min: 50,   max: 99.99, bonus: 20 },
    { min: 100,  max: null,  bonus: 25 },
  ],
  ARS: [
    { min: 0,      max: 999,    bonus: 0  },
    { min: 1000,   max: 9999,   bonus: 5  },
    { min: 10000,  max: 24999,  bonus: 10 },
    { min: 25000,  max: 49999,  bonus: 15 },
    { min: 50000,  max: 99999,  bonus: 20 },
    { min: 100000, max: null,   bonus: 25 },
  ],
  CLP: [
    { min: 0,      max: 999,    bonus: 0  },
    { min: 1000,   max: 9999,   bonus: 5  },
    { min: 10000,  max: 24999,  bonus: 10 },
    { min: 25000,  max: 49999,  bonus: 15 },
    { min: 50000,  max: 99999,  bonus: 20 },
    { min: 100000, max: null,   bonus: 25 },
  ],
};

const QUICK_AMOUNTS = {
  USD: [5, 10, 25, 50, 100],
  ARS: [1000, 5000, 10000, 25000, 100000],
  CLP: [5000, 10000, 25000, 50000, 100000],
};

function getBonus(currency, amount) {
  const tables = BONUS_TABLES[currency] || BONUS_TABLES.USD;
  for (const t of tables) {
    if (amount >= t.min && (t.max === null || amount <= t.max)) return t.bonus;
  }
  return 0;
}

const DonatePage = () => {
  const { user, loading } = useAuth();
  const { data: t } = useContentObject("account.donate");
  const { data: cfg } = useContentObject("donate.config");

  const enabledMethods = useMemo(() => {
    const resolved = METHODS.map((m) => ({
      ...m,
      name: (cfg[`name_${m.id}`] || "").trim() || m.name,
      desc: (cfg[`desc_${m.id}`] || "").trim() || m.desc,
    }));
    const list = resolved.filter((m) => cfg[`method_${m.id}`] !== false);
    return list.length ? list : resolved;
  }, [cfg]);

  const [method, setMethod] = useState(enabledMethods[0]);
  const currentMethod = useMemo(
    () => enabledMethods.find((m) => m.id === method.id) || method,
    [enabledMethods, method]
  );
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [network, setNetwork] = useState("TRC20"); // solo crypto
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Si el metodo seleccionado fue deshabilitado, saltar al primero disponible
  useEffect(() => {
    if (!enabledMethods.some((m) => m.id === method.id)) {
      setMethod(enabledMethods[0]);
    }
  }, [enabledMethods, method]);

  useEffect(() => {
    setAmount("");
    setError("");
  }, [method]);

  const calc = useMemo(() => {
    const n = parseFloat(amount) || 0;
    const meta = CURRENCY_META[method.currency] || CURRENCY_META.USD;
    const rate = rateForMethod(cfg, method.id, method.currency);
    const baseCoins = Math.floor(n * rate.ratio);
    const bonusPct = getBonus(method.currency, n);
    const bonusCoins = Math.floor((baseCoins * bonusPct) / 100);
    const totalCoins = baseCoins + bonusCoins;
    const rateLabel = method.id === "crypto"
      ? `${rate.amount} USDT = ${rate.coins.toLocaleString()} coins`
      : `${rate.amount} ${meta.label} = ${rate.coins.toLocaleString()} coins`;
    return {
      n,
      baseCoins,
      bonusPct,
      bonusCoins,
      totalCoins,
      info: { ...meta, ratio: rate.ratio },
      rate,
      rateLabel,
    };
  }, [amount, method, cfg]);

  if (loading) return <main className="bg-[#0a0a0d] min-h-screen flex items-center justify-center text-[#8a8a92]">Cargando...</main>;
  if (!user) return <Navigate to="/account" replace />;

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = calc.n > 0 && validEmail && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const order = await donateApi.create({
        method: method.id,
        amount: calc.n,
        currency: method.currency,
        email: email.trim(),
        network: method.id === "crypto" ? network : undefined,
        fingerprint: navigator.userAgent.substr(0, 60),
      });
      if (order.payment_url) {
        window.location.href = order.payment_url;
      } else {
        setError("No se recibio URL de pago. Intenta nuevamente.");
        setSubmitting(false);
      }
    } catch (err) {
      setError(err.message || "Error al crear la orden");
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[#0a0a0d] min-h-screen pt-32 pb-20">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Link to="/account/panel" className="inline-flex items-center gap-2 text-xs text-[#b8b3a8] hover:text-[#d4a843] mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al panel
        </Link>

        <div className="ornament"><span>{t.eyebrow}</span></div>
        <h1 className="section-title mt-3 text-3xl md:text-4xl mb-10">{t.title}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Method */}
            <section className="glass rounded-sm p-6">
              <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-4">1. {t.method_title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {enabledMethods.map(m => {
                  const Icon = m.icon;
                  const active = method.id === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMethod(m)}
                      className={`flex items-center gap-3 p-4 rounded-sm border text-left transition-all ${
                        active
                          ? "bg-[rgba(212,168,67,0.12)] border-[#d4a843]"
                          : "bg-[#14141a] border-[rgba(212,168,67,0.15)] hover:border-[rgba(212,168,67,0.4)]"
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${active ? "bg-[#d4a843] text-[#1a1408]" : "bg-[#21212b] text-[#d4a843]"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-display font-semibold text-sm ${active ? "text-[#f5c542]" : "text-[#f0ebe0]"}`}>{m.name}</div>
                        <div className="text-[10px] text-[#8a8a92] truncate">{m.desc}</div>
                      </div>
                      <span className="font-mono text-[10px] text-[#d4a843]">{m.currency}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Step 2: Amount */}
            <section className="glass rounded-sm p-6">
              <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-4">2. {t.amount_title} en {calc.info.label}</h3>

              <div className="flex flex-wrap gap-2 mb-4">
                {QUICK_AMOUNTS[method.currency].map(amt => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAmount(String(amt))}
                    className={`px-3.5 py-1.5 rounded-sm font-mono text-xs border transition-all ${
                      String(amt) === amount
                        ? "bg-[rgba(212,168,67,0.15)] text-[#f5c542] border-[#d4a843]"
                        : "bg-[#14141a] text-[#b8b3a8] border-[rgba(212,168,67,0.15)] hover:border-[rgba(212,168,67,0.4)]"
                    }`}
                  >
                    {calc.info.symbol}{amt.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#d4a843] font-display font-bold text-lg">
                  {calc.info.symbol}
                </span>
                <Input
                  type="number"
                  step={method.currency === "USD" ? "0.01" : "1"}
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-10 h-14 bg-[#111118] border-[rgba(212,168,67,0.2)] focus:border-[#d4a843] text-[#f0ebe0] font-mono text-xl"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8a8a92] font-mono text-sm">
                  {calc.info.label}
                </span>
              </div>
            </section>

            {method.id === "crypto" && (
              <section className="glass rounded-sm p-6" data-testid="crypto-network-section">
                <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-2">3. Red blockchain</h3>
                <p className="text-xs text-[#8a8a92] mb-4">Elegi la red de tu wallet. Las comisiones de red varian.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { id: "TRC20", label: "USDT TRC20", fee: "Tron · ~1 USDT", hint: "Lemon, Bello, Telegram" },
                    { id: "BEP20", label: "USDT BEP20", fee: "BSC · ~0.3 USDT", hint: "Skipo, Binance" },
                    { id: "ERC20", label: "USDT ERC20", fee: "Ethereum · ~5-15 USDT", hint: "MetaMask" },
                    { id: "POLYGON", label: "USDT Polygon", fee: "Polygon · ~0.01 USDT", hint: "Bajas fees" },
                  ].map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setNetwork(n.id)}
                      data-testid={`network-${n.id}`}
                      className={`p-3 rounded-sm border text-left transition-all ${
                        network === n.id
                          ? "bg-[rgba(212,168,67,0.15)] border-[#d4a843]"
                          : "bg-[#14141a] border-[rgba(212,168,67,0.15)] hover:border-[rgba(212,168,67,0.4)]"
                      }`}
                    >
                      <div className={`text-sm font-medium ${network === n.id ? "text-[#f5c542]" : "text-[#f0ebe0]"}`}>{n.label}</div>
                      <div className="text-[10px] text-[#8a8a92] mt-1">{n.fee}</div>
                      <div className="text-[10px] text-[#7e7768] mt-0.5">{n.hint}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Step 3: Email */}
            <section className="glass rounded-sm p-6">
              <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-2">{method.id === "crypto" ? "4" : "3"}. {t.email_title}</h3>
              <p className="text-xs text-[#8a8a92] mb-4">{t.email_note}</p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a92]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  required
                  className="pl-10 h-11 bg-[#111118] border-[rgba(212,168,67,0.2)] focus:border-[#d4a843] text-[#f0ebe0] font-mono"
                />
              </div>
            </section>

            {error && (
              <div className="flex items-start gap-3 p-4 bg-[#260d0d]/60 border border-[#b03030]/40 rounded-sm text-sm text-[#ef4444]">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </form>

          {/* Summary sidebar */}
          <aside className="lg:sticky lg:top-32 self-start">
            <div className="glass rounded-sm p-6">
              <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-5">Resumen</h3>

              <dl className="space-y-3 text-sm">
                <Row label="Cuenta" value={user.account} mono />
                <Row label="Método" value={currentMethod.name} />
                <Row label="Tasa" value={calc.rateLabel} mono />
                <Row label="Monto" value={calc.n > 0 ? `${calc.info.symbol}${calc.n.toLocaleString()} ${calc.info.label}` : "—"} />
                <hr className="border-[rgba(212,168,67,0.1)]" />
                <Row label="Coins base" value={calc.baseCoins.toLocaleString()} mono />
                <Row label={`Bonus +${calc.bonusPct}%`} value={`+${calc.bonusCoins.toLocaleString()}`} accent />
                <hr className="border-[rgba(212,168,67,0.15)]" />
                <div className="flex items-center justify-between pt-2">
                  <dt className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843]">Total Coins</dt>
                  <dd className="font-display font-extrabold text-2xl text-[#f5c542]" style={{ textShadow: "0 0 24px rgba(212,168,67,0.4)" }}>
                    {calc.totalCoins.toLocaleString()}
                  </dd>
                </div>
              </dl>

              <button
                type="submit"
                disabled={!canSubmit}
                onClick={handleSubmit}
                className="btn-gold w-full py-4 mt-6 text-xs rounded-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando orden...</>
                  : <><ChevronRight className="w-4 h-4" /> Continuar al pago</>}
              </button>

              <div className="mt-5 flex items-start gap-2 text-[10px] text-[#8a8a92] leading-relaxed">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <span>{(t.security_note || "").replace(/\{method\}/g, currentMethod.name)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

const Row = ({ label, value, mono, accent }) => (
  <div className="flex items-center justify-between">
    <dt className="text-[#8a8a92] text-xs">{label}</dt>
    <dd className={`${mono ? "font-mono" : "font-display"} text-sm ${accent ? "text-[#10b981]" : "text-[#f0ebe0]"}`}>{value}</dd>
  </div>
);

export default DonatePage;
